import { useEffect, useState } from "react";
import useDebounce from "hooks/useDebounce";
import {
  BedDouble,
  Building2,
  Check,
  CheckCircle2,
  GripVertical,
  Loader2,
  MapPin,
  Plus,
  Search,
  Trash2,
  User,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import { Input } from "ui/input";
import {
  RoomAllocationApplicant,
  RoomAvailability,
  saveRoomAllocations,
  deleteRoomAllocation,
  deleteShortlistedApplicant,
  getVisaApplicants,
} from "services/tourApprovalService";
import { AxiosError } from "axios";

interface ApprovedApplicant {
  id: string;
  referenceId?: string | null;
  name: string;
  itsNumber: string;
  gender: string;
  age: number;
  status: string;
  photo: string;
  hofITS: string;
  applicantType: string;
  needsAccommodation: boolean;
}

interface ShortlistedApplicant extends ApprovedApplicant {
  publicId: string;
}

interface Room {
  id: string;
  number: string;
  type: string;
  occupancy: number;
  maxOccupancy: number;
  requestedNumber: number;
  applicants: RoomAllocationApplicant[];
  venue: string;
  city: string;
  propertyRoomTypePublicId: string;
  roomSelectionPublicId: string;
  propertyRoomTypeId: number;
  propertyId: number;
  venueId: number;
  propertyPublicId: string;
  roomAllocationPublicId?: string;
  status: string;
}

interface RoomsAllocationProps {
  rooms: Room[];
  setRooms: React.Dispatch<React.SetStateAction<Room[]>>;
  onAddApplicants: (applicantIds: string[]) => void;
  getGroupedApplicantsByFamily: () => Record<string, ShortlistedApplicant[]>;
  approvedApplicants: ApprovedApplicant[];
  shortlistedApplicants: ShortlistedApplicant[];
  roomAvailabilities: RoomAvailability[];
  tourPublicId: string;
  createdBy: number;
  reloadRoomAllocations: () => Promise<void>;
  reloadShortlistedApplicants: () => Promise<void>;
  isReadOnly?: boolean;

  venueSchedules: {
    venueId: number | null;
    fromDate: string;
    toDate: string;
  }[];
}

const FAMILY_STYLES = [
  { wrap: "border-sky-300 bg-sky-50/70", code: "bg-sky-500" },
  { wrap: "border-rose-300 bg-rose-50/70", code: "bg-rose-500" },
  { wrap: "border-violet-300 bg-violet-50/70", code: "bg-violet-500" },
  { wrap: "border-emerald-300 bg-emerald-50/70", code: "bg-emerald-500" },
];

const ROOM_TYPE_STYLE: Record<string, string> = {
  Double: "border-indigo-200 bg-indigo-100 text-indigo-700",
  Triple: "border-violet-200 bg-violet-100 text-violet-700",
  Quad: "border-cyan-200 bg-cyan-100 text-cyan-700",
  "Shared Gents": "border-blue-200 bg-blue-100 text-blue-700",
  "Shared Ladies": "border-pink-200 bg-pink-100 text-pink-700",
};

const PROPERTY_META: Record<string, { tone: string; icon: string }> = {
  Karbala: {
    tone: "border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50",
    icon: "🏨",
  },
  Najaf: {
    tone: "border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50",
    icon: "🏛️",
  },
};

function GenderAvatar({
  gender,
  name,
  size = "md",
}: {
  gender: string;
  name: string;
  size?: "sm" | "md" | "lg";
}) {
  const isFemale = gender.toLowerCase() === "female";
  const sizeClass =
    size === "sm"
      ? "h-7 w-7 text-sm"
      : size === "lg"
        ? "h-10 w-10 text-xl"
        : "h-8 w-8 text-base";
  return (
    <span
      title={`${name} · ${gender}`}
      aria-label={`${name}, ${gender}`}
      className={`flex shrink-0 items-center justify-center rounded-full border-2 border-white shadow-sm ${sizeClass} ${
        isFemale ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"
      }`}
    >
      {isFemale ? "👩" : "👨"}
    </span>
  );
}

export default function RoomsAllocationNew({
  rooms,
  setRooms,
  onAddApplicants,
  getGroupedApplicantsByFamily,
  approvedApplicants,
  shortlistedApplicants,
  roomAvailabilities,
  tourPublicId,
  createdBy,
  reloadRoomAllocations,
  reloadShortlistedApplicants,
  venueSchedules,
  isReadOnly = false,
}: RoomsAllocationProps) {
  const [showAddApplicantDialog, setShowAddApplicantDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 400);
  const [matchingApplicantIds, setMatchingApplicantIds] = useState<Set<string>>(new Set());
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const isSearching = loadingApplicants || searchQuery !== debouncedSearchQuery;

  useEffect(() => {
    if (!showAddApplicantDialog || !tourPublicId) return;
    let cancelled = false;
    const loadAvailableApplicants = async () => {
      setLoadingApplicants(true);
      try {
        const response = await getVisaApplicants(tourPublicId, {
          searchText: debouncedSearchQuery.trim() || undefined,
        });
        if (!cancelled) {
          setMatchingApplicantIds(new Set(response.data.map((applicant) => String(applicant.visaApplicationId))));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to search visa applicants", error);
          toast.error("Failed to load applicants");
          setMatchingApplicantIds(new Set());
        }
      } finally {
        if (!cancelled) setLoadingApplicants(false);
      }
    };
    void loadAvailableApplicants();
    return () => { cancelled = true; };
  }, [showAddApplicantDialog, tourPublicId, debouncedSearchQuery]);
  
  const [pendingApplicantIds, setPendingApplicantIds] = useState<string[]>([]);
  const [dragOverRoomId, setDragOverRoomId] = useState<string | null>(null);
  const [dragOverVenue, setDragOverVenue] = useState<string | null>(null);
  const [selectedAllocationIds, setSelectedAllocationIds] = useState<string[]>(
    [],
  );

  const groupedApplicants = getGroupedApplicantsByFamily();
  const families = Object.entries(groupedApplicants).filter(
    ([familyId, members]) => familyId !== "no-family" && members.length >= 2,
  );
  const individualApplicants = [
    ...(groupedApplicants["no-family"] || []),
    ...Object.entries(groupedApplicants)
      .filter(
        ([familyId, members]) => familyId !== "no-family" && members.length < 2,
      )
      .flatMap(([, members]) => members),
  ];
  const familyKeyByApplicantId = Object.entries(groupedApplicants).reduce<
    Record<string, string | null>
  >((keys, [familyId, members]) => {
    members.forEach((member) => {
      keys[member.id] = familyId === "no-family" ? null : familyId;
    });
    return keys;
  }, {});

  const availableApplicants = approvedApplicants.filter(
    (applicant) =>
      !shortlistedApplicants.some((s) => s.id === applicant.id) &&
      matchingApplicantIds.has(applicant.id),
  );

  const venueCities = Array.from(new Set(rooms.map((room) => room.city)));

  function getApplicant(applicantId: string): ShortlistedApplicant | undefined {
    return shortlistedApplicants.find(
      (applicant) => applicant.id === applicantId,
    );
  }

  function isInfant(applicantId: string) {
    const applicant = getApplicant(applicantId);

    return applicant?.applicantType?.toLowerCase() === "infant";
  }

  const assignedCount = rooms.reduce(
    (total, room) => total + room.applicants.length,
    0,
  );
  const requiredAssignmentCount =
    shortlistedApplicants.length * venueCities.length;
  const availableRooms = rooms.filter((room) => {
    const occupied = room.applicants.filter(
      (a) => !isInfant(a.visaApplicationId.toString()),
    ).length;
    return occupied === 0;
  }).length;

  const partialRooms = rooms.filter((room) => {
    const occupied = room.applicants.filter(
      (a) => !isInfant(a.visaApplicationId.toString()),
    ).length;
    return occupied > 0 && occupied < room.maxOccupancy;
  }).length;

  const fullRooms = rooms.filter((room) => {
    const occupied = room.applicants.filter(
      (a) => !isInfant(a.visaApplicationId.toString()),
    ).length;
    return occupied >= room.maxOccupancy;
  }).length;

  const closeApplicantDialog = () => {
    setShowAddApplicantDialog(false);
    setSearchQuery("");
    setPendingApplicantIds([]);
  };

  const getAssignedRooms = (applicantId: string) =>
    rooms.filter((room) =>
      room.applicants.some((a) => a.visaApplicationId === Number(applicantId)),
    );

  const isAssignedInVenue = (applicantId: string, city: string) =>
    rooms.some(
      (room) =>
        room.city === city &&
        room.applicants.some(
          (a) => a.visaApplicationId === Number(applicantId),
        ),
    );

  const isFullyAssigned = (applicantId: string) =>
    venueCities.length > 0 &&
    venueCities.every((city) => isAssignedInVenue(applicantId, city));

  const startApplicantDrag = (
    event: React.DragEvent<HTMLElement>,
    applicantIds: string[],
    type: "family" | "individual",
  ) => {
    if (isReadOnly) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("applicants", JSON.stringify(applicantIds));
    event.dataTransfer.setData("type", type);
  };

  const saveAllocation = async (updatedRooms: Room[]) => {
    const payload = {
      roomAllocations: updatedRooms
        .filter((room) => room.applicants.length > 0)
        .map((room) => {
          const uniqueApplicants = uniqueApplicantsByVisaId(room.applicants);

          return {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            roomAllocationPublicId: room.roomAllocationPublicId!,
            applicants: uniqueApplicants.map((applicant) => ({
              visaApplicationId: Number(applicant.visaApplicationId),
            })),
          };
        }),
    };

    await saveRoomAllocations(tourPublicId, createdBy, payload);
  };

  const toggleAllocationSelection = (applicantId: string) => {
    if (isFullyAssigned(applicantId)) return;
    setSelectedAllocationIds((current) =>
      current.includes(applicantId)
        ? current.filter((id) => id !== applicantId)
        : [...current, applicantId],
    );
  };

  const dragIdsForApplicant = (applicantId: string) =>
    selectedAllocationIds.includes(applicantId)
      ? selectedAllocationIds.filter((id) => !isFullyAssigned(id))
      : [applicantId];

  const respectsMehramSharing = (
    room: Room,
    incomingApplicantIds: string[],
  ) => {
    const existingApplicants = room.applicants
      .map((a) => getApplicant(a.visaApplicationId.toString()))
      .filter(Boolean) as ShortlistedApplicant[];

    const newApplicants = incomingApplicantIds
      .map(getApplicant)
      .filter(Boolean) as ShortlistedApplicant[];

    const applicants = [...existingApplicants, ...newApplicants];
    if (applicants.length < 2) return true;

    const genders = new Set(applicants.map((applicant) => applicant.gender));
    if (genders.size === 1) return true;

    const familyIds = applicants.map(
      (applicant) => familyKeyByApplicantId[applicant.id] ?? applicant.hofITS,
    );
    return familyIds.every(Boolean) && new Set(familyIds).size === 1;
  };

  const validateRoomDrop = (room: Room, applicantIds: string[]) => {
    if (!applicantIds.length) return "No applicants selected";
    const occupiedCount = room.applicants.filter(
      (a) => !isInfant(a.visaApplicationId.toString()),
    ).length;

    const incomingCount = applicantIds.filter((id) => !isInfant(id)).length;

    if (occupiedCount + incomingCount > room.maxOccupancy) {
      return "Room capacity exceeded";
    }
    const applicants = applicantIds
      .map(getApplicant)
      .filter(Boolean) as ShortlistedApplicant[];
    if (
      room.type === "Shared Gents" &&
      applicants.some((applicant) => applicant.gender !== "Male")
    ) {
      return "Only male applicants can be assigned to Shared Gents";
    }
    if (
      room.type === "Shared Ladies" &&
      applicants.some((applicant) => applicant.gender !== "Female")
    ) {
      return "Only female applicants can be assigned to Shared Ladies";
    }
    if (!respectsMehramSharing(room, applicantIds)) {
      return "Mixed-gender sharing is allowed only for applicants in the same Mehram family. Non-clubbed applicants can share only with the same gender.";
    }
    return "";
  };

  const uniqueApplicantsByVisaId = (
    applicants: RoomAllocationApplicant[],
  ): RoomAllocationApplicant[] => {
    const seen = new Set<number>();

    return applicants.filter((applicant) => {
      const visaApplicationId = Number(applicant.visaApplicationId);

      if (seen.has(visaApplicationId)) {
        return false;
      }

      seen.add(visaApplicationId);
      return true;
    });
  };

  const handleRoomDrop = async (
    e: React.DragEvent<HTMLDivElement>,
    room: Room,
  ) => {
    if (isReadOnly) return;
    e.preventDefault();
    const payload = e.dataTransfer.getData("applicants");
    if (!payload) return;
    const requestedApplicantIds: string[] = JSON.parse(payload);
    const uniqueRequestedApplicantIds = [
      ...new Set(requestedApplicantIds.map(String)),
    ];
    const existingApplicantIds = new Set(
      room.applicants.map((applicant) => Number(applicant.visaApplicationId)),
    );
    const applicantIds = uniqueRequestedApplicantIds.filter(
      (id) => !existingApplicantIds.has(Number(id)),
    );
    if (!applicantIds.length) {
      toast.error("Selected applicants are already assigned to this room");
      return;
    }
    const existingApplicants = uniqueApplicantsByVisaId(room.applicants);
    const roomForValidation = {
      ...room,
      applicants: existingApplicants,
    };
    const validationMessage = validateRoomDrop(roomForValidation, applicantIds);
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    const newApplicants: RoomAllocationApplicant[] = applicantIds.map(
      (id: string) => ({
        tourRoomAllocationApplicantId: 0,
        publicId: "",
        tourRoomAllocationId: 0,
        visaApplicationId: Number(id),
        itsNo: "",
        applicantName: "",
        isActive: true,
      }),
    );

    const finalApplicants = uniqueApplicantsByVisaId([
      ...existingApplicants,
      ...newApplicants,
    ]);

    const occupancy = finalApplicants.filter(
      (applicant) => !isInfant(applicant.visaApplicationId.toString()),
    ).length;

    const updatedRooms = rooms.map((r) =>
      r.id === room.id
        ? {
            ...r,
            applicants: finalApplicants,
            occupancy,
          }
        : r,
    );

    setRooms(updatedRooms);

    try {
      await saveAllocation(updatedRooms);

      await reloadRoomAllocations();

      toast.success(
        applicantIds.length > 1
          ? "Family assigned to room"
          : "Applicant assigned to room",
      );
    } catch (error: unknown) {
      setRooms(rooms);

      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.message ??
            "Failed to assign applicant to room.",
        );
      } else {
        toast.error("Failed to assign applicant to room.");
      }
    }
  };

  const handleVenueDrop = async (
    event: React.DragEvent<HTMLDivElement>,
    city: string,
  ) => {
    if (isReadOnly) return;
    event.preventDefault();
    setDragOverVenue(null);

    const payload = event.dataTransfer.getData("applicants");
    if (!payload) return;

    const requestedApplicantIds = JSON.parse(payload) as string[];

    const applicantIds = requestedApplicantIds.filter(
      (id) => !isAssignedInVenue(id, city),
    );

    if (!applicantIds.length) {
      toast.error(`Selected applicants are already assigned in ${city}`);
      return;
    }

    const plannedRooms = rooms.map((room) => ({
      ...room,
      applicants: [...room.applicants],
    }));

    const cannotPlace = applicantIds.some((applicantId) => {
      const applicant = getApplicant(applicantId);
      if (!applicant) return true;

      const compatibleIndexes = plannedRooms
        .map((room, index) => ({ room, index }))
        .filter(({ room }) => {
          const occupiedCount = room.applicants.filter(
            (a) => !isInfant(a.visaApplicationId.toString()),
          ).length;

          if (room.city !== city || occupiedCount >= room.maxOccupancy)
            return false;

          if (room.type === "Shared Gents" && applicant.gender !== "Male")
            return false;

          if (room.type === "Shared Ladies" && applicant.gender !== "Female")
            return false;

          if (!respectsMehramSharing(room, [applicantId])) return false;

          return true;
        })
        .sort(({ room: first }, { room: second }) => {
          const preferred =
            applicant.gender === "Male" ? "Shared Gents" : "Shared Ladies";

          return (
            Number(second.type === preferred) - Number(first.type === preferred)
          );
        });

      const target = compatibleIndexes[0];

      if (!target) return true;

      plannedRooms[target.index].applicants.push({
        tourRoomAllocationApplicantId: 0,
        publicId: "",
        tourRoomAllocationId: 0,
        visaApplicationId: Number(applicantId),
        itsNo: "",
        applicantName: "",
        isActive: true,
      });

      plannedRooms[target.index].occupancy = plannedRooms[
        target.index
      ].applicants.filter(
        (a) => !isInfant(a.visaApplicationId.toString()),
      ).length;

      return false;
    });

    if (cannotPlace) {
      toast.error(`All selected applicants cannot be accommodated in ${city}`);
      return;
    }

    const previousRooms = rooms;

    setRooms(plannedRooms);

    try {
      await saveAllocation(plannedRooms);
      await reloadRoomAllocations();

      setSelectedAllocationIds((current) =>
        current.filter((id) => {
          if (!applicantIds.includes(id)) return true;

          return venueCities.some(
            (venueCity) =>
              venueCity !== city && !isAssignedInVenue(id, venueCity),
          );
        }),
      );

      toast.success(
        `${applicantIds.length} applicants assigned within ${city}`,
      );
    } catch (error) {
      setRooms(previousRooms);
      toast.error("Failed to assign applicants");
    }
  };

  const removeApplicantFromRoom = async (
    roomId: string,
    allocationApplicant: RoomAllocationApplicant,
  ) => {
    try {
      await deleteRoomAllocation(tourPublicId, allocationApplicant.publicId);

      setRooms((prev) =>
        prev.map((room) =>
          room.id === roomId
            ? {
                ...room,
                applicants: room.applicants.filter(
                  (a) => a.publicId !== allocationApplicant.publicId,
                ),
                occupancy: room.applicants.filter(
                  (a) =>
                    a.publicId !== allocationApplicant.publicId &&
                    !isInfant(a.visaApplicationId.toString()),
                ).length,
              }
            : room,
        ),
      );

      await reloadRoomAllocations();

      toast.success("Applicant removed from room");
    } catch (error) {
      toast.error("Failed to remove applicant");
    }
  };

  const removeApplicantFromShortlist = async (applicantId: string) => {
    const applicant = shortlistedApplicants.find((a) => a.id === applicantId);

    if (!applicant) return;

    try {
      await deleteShortlistedApplicant(applicant.publicId);

      await reloadShortlistedApplicants();
      await reloadRoomAllocations();

      toast.success("Applicant removed from shortlist");
    } catch (error) {
      toast.error("Failed to remove applicant from shortlist");
    }
  };

  const renderRoomCard = (room: Room) => {
    const occupiedCount = room.applicants.filter(
      (a) => !isInfant(a.visaApplicationId.toString()),
    ).length;

    const isFull = occupiedCount >= room.maxOccupancy;
    const isPartial = occupiedCount > 0 && !isFull;
    const isDragTarget = dragOverRoomId === room.id;
    const statusClass = isFull
      ? "border-emerald-300 bg-emerald-50/40"
      : isPartial
        ? "border-amber-300 bg-amber-50/30"
        : "border-slate-200 bg-white";

    return (
      <div
        key={room.id}
        onDragOver={(event) => {
          if (isReadOnly) return;
          if (!isFull) {
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            setDragOverRoomId(room.id);
          }
        }}
        onDragLeave={() =>
          setDragOverRoomId((current) => (current === room.id ? null : current))
        }
        onDrop={isReadOnly ? undefined : (event) => handleRoomDrop(event, room)}
        className={`rounded-xl border-2 p-3 shadow-sm transition-all ${statusClass} ${
          isDragTarget
            ? "scale-[1.01] border-sky-500 bg-sky-50 shadow-md ring-2 ring-sky-100"
            : ""
        }`}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div>
            <Badge
              className={`text-xs ${ROOM_TYPE_STYLE[room.type] || "border-slate-200 bg-slate-100 text-slate-700"}`}
            >
              {room.type}
            </Badge>
          </div>
          <div className="text-right">
            <div
              className={`text-lg font-bold ${isFull ? "text-emerald-600" : isPartial ? "text-amber-600" : "text-sky-600"}`}
            >
              {occupiedCount}/{room.maxOccupancy}
            </div>
            <div className="text-[9px] text-slate-400">Occupancy</div>
          </div>
        </div>

        <div className="mb-2 flex min-h-8 items-center gap-1">
          {Array.from({ length: room.maxOccupancy }).map((_, index) => {
            const allocationApplicant = room.applicants[index];

            const applicant = allocationApplicant
              ? shortlistedApplicants.find(
                  (a) =>
                    a.id === allocationApplicant.visaApplicationId.toString(),
                )
              : undefined;
            return applicant ? (
              <GenderAvatar
                key={applicant.id}
                gender={applicant.gender}
                name={applicant.name}
                size="sm"
              />
            ) : (
              <span
                key={`${room.id}-${index}`}
                className="h-7 w-7 rounded-full border border-slate-200 bg-slate-100"
              />
            );
          })}
        </div>

        {room.applicants.length === 0 ? (
          <div
            className={`flex min-h-16 items-center justify-center rounded-lg border border-dashed text-center text-[10px] ${isDragTarget ? "border-sky-400 bg-white text-sky-600" : "border-slate-300 bg-slate-50/70 text-slate-400"}`}
          >
            <div>
              {!isReadOnly && <GripVertical className="mx-auto mb-1 h-4 w-4" />}
              Drop applicants here
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {room.applicants.map((allocationApplicant) => {
              const applicant = shortlistedApplicants.find(
                (a) =>
                  a.id === allocationApplicant.visaApplicationId.toString(),
              );
              if (!applicant) return null;
              return (
                <div
                  key={`${room.id}-${allocationApplicant.visaApplicationId}`}
                  className="flex items-center gap-2 rounded-lg border border-white bg-white/90 p-1.5"
                >
                  <GenderAvatar
                    gender={applicant.gender}
                    name={applicant.name}
                    size="sm"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[10px] font-semibold text-slate-900">
                      {applicant.name}
                    </div>
                    <div className="text-[9px] text-slate-400">
                      ITS {applicant.itsNumber}
                    </div>
                  </div>
                  <button
                    type="button"
                    title="Remove from room"
                    disabled={isReadOnly}
                    onClick={() => {
                      if (isReadOnly) return;
                      removeApplicantFromRoom(room.id, allocationApplicant);
                    }}
                    className={`rounded border border-red-200 p-1 transition-colors ${
                      isReadOnly
                        ? "cursor-not-allowed text-red-400 opacity-50"
                        : "text-red-500 hover:bg-red-50"
                    }`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 overflow-x-hidden rounded-2xl bg-gradient-to-br from-sky-50/80 via-white to-blue-50/60 p-3 sm:p-4">
      <Card className="gap-0 border-sky-200/70 bg-white/90 shadow-sm">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-stretch gap-2 overflow-x-auto">
            {[
              {
                label: "Total Rooms",
                value: rooms.length,
                tone: "text-sky-600 bg-sky-50",
                icon: BedDouble,
              },
              {
                label: "Available",
                value: availableRooms,
                tone: "text-emerald-600 bg-emerald-50",
                icon: CheckCircle2,
              },
              {
                label: "Partial / Full",
                value: `${partialRooms} / ${fullRooms}`,
                tone: "text-amber-600 bg-amber-50",
                icon: Building2,
              },
              {
                label: "Venue Allocations",
                value: `${assignedCount}/${requiredAssignmentCount}`,
                tone: "text-violet-600 bg-violet-50",
                icon: Users,
              },
            ].map(({ label, value, tone, icon: Icon }) => (
              <div
                key={label}
                className="flex min-w-40 flex-1 items-center gap-3 rounded-xl border border-sky-100 bg-white px-3 py-2.5 shadow-sm"
              >
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-lg font-bold leading-none text-slate-900">
                    {value}
                  </div>
                  <div className="mt-1 text-[10px] font-medium text-slate-500">
                    {label}
                  </div>
                </div>
              </div>
            ))}
            <Button
              size="sm"
              disabled={isReadOnly}
              onClick={() => {
                if (isReadOnly) return;
                setLoadingApplicants(true);
                setShowAddApplicantDialog(true);
              }}
              className="h-auto min-w-36 shrink-0 self-stretch bg-sky-600 text-white hover:bg-sky-700"
            >
              <UserPlus className="mr-1.5 h-4 w-4" /> Add Applicants
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-12 items-stretch gap-4 lg:h-[calc(100vh-360px)] min-h-0">
        <div className="col-span-12 space-y-4 lg:col-span-8 min-h-0 overflow-y-auto pr-2 overscroll-contain">
          {["Karbala", "Najaf"].map((city) => {
            const cityRooms = rooms.filter((room) => room.city === city);
            if (!cityRooms.length) return null;
            const meta = PROPERTY_META[city] || PROPERTY_META.Karbala;
            const citySchedule = venueSchedules.find((schedule) =>
              cityRooms.some((room) => room.venueId === schedule.venueId),
            );
            const formatDate = (date: string) =>
              new Date(date).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              });

            const venueDates = citySchedule
              ? `${formatDate(citySchedule.fromDate)} → ${formatDate(citySchedule.toDate)}`
              : "";
            const cityAvailable = cityRooms.filter(
              (room) => room.applicants.length === 0,
            ).length;
            const cityPartial = cityRooms.filter((room) => {
              const occupied = room.applicants.filter(
                (a) => !isInfant(a.visaApplicationId.toString()),
              ).length;

              return occupied > 0 && occupied < room.maxOccupancy;
            }).length;
            const cityFull = cityRooms.filter((room) => {
              const occupied = room.applicants.filter(
                (a) => !isInfant(a.visaApplicationId.toString()),
              ).length;

              return occupied >= room.maxOccupancy;
            }).length;
            const propertyGroups = Array.from(
              new Set(cityRooms.map((room) => room.venue)),
            ).map((propertyName) => ({
              propertyName,
              propertyRooms: cityRooms.filter(
                (room) => room.venue === propertyName,
              ),
            }));
            return (
              <Card
                key={city}
                className="gap-0 border-sky-200/70 bg-white/90 shadow-md"
                style={{ marginTop: "10px" }}
              >
                <CardContent className="p-3 sm:p-4">
                  <div
                    onDragOver={(event) => {
                      if (isReadOnly) return;
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                      setDragOverVenue(city);
                    }}
                    onDragLeave={() =>
                      setDragOverVenue((current) =>
                        current === city ? null : current,
                      )
                    }
                    onDrop={
                      isReadOnly
                        ? undefined
                        : (event) => handleVenueDrop(event, city)
                    }
                    className={`mb-4 flex flex-col gap-3 rounded-xl border p-3 transition-all sm:flex-row sm:items-center sm:justify-between ${meta.tone} ${
                      dragOverVenue === city
                        ? "border-sky-500 ring-2 ring-sky-200 shadow-md"
                        : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-xl shadow-sm">
                        {meta.icon}
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-semibold text-sky-950">{city}</h3>
                          <Badge className="border-sky-200 bg-white/80 text-[9px] text-sky-700">
                            Venue
                          </Badge>
                        </div>
                        <p className="mt-0.5 flex items-center gap-1 text-[10px] text-slate-600">
                          <MapPin className="h-3 w-3" /> {propertyGroups.length}{" "}
                          {propertyGroups.length === 1
                            ? "property"
                            : "properties"}{" "}
                          · {venueDates}
                        </p>
                        <p className="mt-1 text-[9px] font-medium text-sky-700">
                          Drop selected applicants here to auto-assign within
                          this venue
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <Badge className="border-emerald-200 bg-emerald-100 text-[9px] text-emerald-700">
                        {cityAvailable} avail
                      </Badge>
                      <Badge className="border-amber-200 bg-amber-100 text-[9px] text-amber-700">
                        {cityPartial} partial
                      </Badge>
                      <Badge className="border-sky-200 bg-sky-100 text-[9px] text-sky-700">
                        {cityFull} full
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {propertyGroups.map(({ propertyName, propertyRooms }) => (
                      <section
                        key={propertyName}
                        className="rounded-xl border border-sky-100 bg-slate-50/60 p-2.5"
                      >
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 text-xs font-semibold text-sky-900">
                            <Building2 className="h-3.5 w-3.5 text-sky-500" />{" "}
                            {propertyName}
                          </div>
                          <Badge className="border-slate-200 bg-white text-[9px] text-slate-600">
                            {propertyRooms.length} room types
                          </Badge>
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                          {propertyRooms.map(renderRoomCard)}
                        </div>
                      </section>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <aside className="col-span-12 space-y-3 lg:col-span-4 min-h-0 overflow-y-auto pr-1 overscroll-contain">
          {selectedAllocationIds.length > 0 && (
            <div
              draggable={!isReadOnly}
              onDragStart={(event) => {
                if (isReadOnly) return;
                startApplicantDrag(event, selectedAllocationIds, "individual");
              }}
              className="cursor-grab rounded-xl border-2 border-dashed border-violet-300 bg-violet-50 p-3 shadow-sm active:cursor-grabbing"
            >
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1.5">
                  {selectedAllocationIds.slice(0, 4).map((applicantId) => {
                    const applicant = getApplicant(applicantId);
                    return applicant ? (
                      <GenderAvatar
                        key={applicant.id}
                        gender={applicant.gender}
                        name={applicant.name}
                        size="sm"
                      />
                    ) : null;
                  })}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-violet-900">
                    {selectedAllocationIds.length} applicants selected
                  </div>
                  <div className="text-[9px] text-violet-600">
                    Drag together to a room or venue
                  </div>
                </div>
                {!isReadOnly && (
                  <GripVertical className="h-4 w-4 text-violet-500" />
                )}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedAllocationIds([]);
                  }}
                  className="rounded border border-violet-200 bg-white p-1 text-violet-600 hover:bg-violet-100"
                  title="Clear selection"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}

          <Card
            className="border-blue-200/70 bg-gradient-to-br from-blue-50/70 to-sky-50/70 shadow-md"
            style={{ gap: "0px" }}
          >
            <CardHeader className="p-3 pb-2">
              <CardTitle className="flex items-center gap-1.5 text-xs text-blue-900">
                <Users className="h-3.5 w-3.5 text-blue-600" /> Tour Groups —
                Pre-assigned
                <Badge className="ml-auto border-blue-200 bg-blue-100 text-[9px] text-blue-700">
                  {families.length}
                </Badge>
              </CardTitle>
              <p className="text-[10px] text-blue-500">
                Drag a complete family or an individual member
              </p>
            </CardHeader>
            <CardContent className="space-y-2 px-3 pb-3">
              {families.map(([familyId, members], index) => {
                const unassignedMembers = members.filter(
                  (member) => !isFullyAssigned(member.id),
                );
                const allAssigned = members.every((member) =>
                  isFullyAssigned(member.id),
                );
                const pendingVenueAssignments = members.reduce(
                  (total, member) =>
                    total +
                    venueCities.filter(
                      (city) => !isAssignedInVenue(member.id, city),
                    ).length,
                  0,
                );
                const style = FAMILY_STYLES[index % FAMILY_STYLES.length];
                return (
                  <div
                    key={familyId}
                    draggable={!isReadOnly && !allAssigned}
                    onDragStart={(event) =>
                      startApplicantDrag(
                        event,
                        [
                          ...new Set(
                            unassignedMembers.map((member) => member.id),
                          ),
                        ],
                        "family",
                      )
                    }
                    className={`rounded-xl border p-2.5 shadow-sm transition ${style.wrap} ${allAssigned ? "opacity-70" : "cursor-grab hover:shadow-md active:cursor-grabbing"}`}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className={`rounded-full px-2.5 py-1 text-[10px] font-bold text-white ${style.code}`}
                      >
                        {familyId}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-bold text-slate-900">
                          HOF: {members[0].itsNumber}
                        </div>
                        <div className="text-[9px] text-slate-500">
                          {pendingVenueAssignments} venue assignments pending
                        </div>
                      </div>
                      {!allAssigned && (
                        <GripVertical className="h-4 w-4 text-sky-500" />
                      )}
                    </div>
                    <div className="space-y-1.5">
                      {members.map((member) => {
                        const assignedRooms = getAssignedRooms(member.id);
                        const fullyAssigned = isFullyAssigned(member.id);
                        return (
                          <div
                            key={member.id}
                            draggable={!isReadOnly && !fullyAssigned}
                            onDragStart={(event) =>
                              startApplicantDrag(
                                event,
                                dragIdsForApplicant(member.id),
                                "individual",
                              )
                            }
                            className={`flex items-center gap-2 rounded-lg border border-white/80 bg-white/80 p-1.5 ${fullyAssigned ? "opacity-75" : selectedAllocationIds.includes(member.id) ? "cursor-grab border-violet-300 ring-1 ring-violet-200" : "cursor-grab hover:bg-white active:cursor-grabbing"}`}
                          >
                            {!fullyAssigned && (
                              <button
                                type="button"
                                onClick={(event) => {
                                  if (isReadOnly) return;

                                  event.stopPropagation();
                                  toggleAllocationSelection(member.id);
                                }}
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${selectedAllocationIds.includes(member.id) ? "border-violet-600 bg-violet-600 text-white" : "border-slate-300 bg-white"}`}
                                aria-label={`Select ${member.name}`}
                              >
                                {selectedAllocationIds.includes(member.id) && (
                                  <Check className="h-3 w-3" />
                                )}
                              </button>
                            )}
                            <GenderAvatar
                              gender={member.gender}
                              name={member.name}
                              size="sm"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[10px] font-medium text-slate-900">
                                {member.name}
                              </div>
                              <div className="text-[9px] text-slate-400">
                                ITS {member.itsNumber} · {member.age}y
                              </div>
                              <div className="mt-0.5 flex flex-wrap gap-0.5">
                                {assignedRooms.map((room) => (
                                  <Badge
                                    key={room.id}
                                    className="border-emerald-200 bg-emerald-100 px-1 py-0 text-[7px] text-emerald-700"
                                  >
                                    {room.city}: {room.type}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            {/* {!fullyAssigned && ( */}
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                disabled={isReadOnly}
                                onClick={async (e) => {
                                  if (isReadOnly) return;

                                  e.stopPropagation();
                                  await removeApplicantFromShortlist(member.id);
                                }}
                                className={`rounded p-1 transition-colors ${
                                  isReadOnly
                                    ? "cursor-not-allowed text-red-400 opacity-50"
                                    : "text-red-500 hover:bg-red-50"
                                }`}
                                title="Remove applicant"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>

                              {!isReadOnly && (
                                <GripVertical className="h-3 w-3 text-sky-400" />
                              )}
                            </div>
                            {/* )} */}
                          </div>
                        );
                      })}
                    </div>
                    {allAssigned && (
                      <div className="mt-2 flex items-center justify-center gap-1 rounded-md border border-emerald-200 bg-white/70 py-1.5 text-[9px] font-medium text-emerald-700">
                        <CheckCircle2 className="h-3 w-3" /> All assigned
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card
            className="border-amber-200/70 bg-gradient-to-br from-amber-50/70 to-yellow-50/70 shadow-md"
            style={{ gap: "0px" }}
          >
            <CardHeader className="p-3 pb-2">
              <CardTitle className="flex items-center gap-1.5 text-xs text-amber-900">
                <User className="h-3.5 w-3.5 text-amber-600" /> Individual
                Applicants
                <Badge className="ml-auto border-amber-200 bg-amber-100 text-[9px] text-amber-700">
                  {individualApplicants.length}
                </Badge>
              </CardTitle>
              <p className="text-[10px] text-amber-600">
                Applicants not clubbed as families
              </p>
            </CardHeader>
            <CardContent className="space-y-2 px-3 pb-3">
              {individualApplicants.map((applicant) => {
                const assignedRooms = getAssignedRooms(applicant.id);
                const fullyAssigned = isFullyAssigned(applicant.id);
                return (
                  <div
                    key={applicant.id}
                    draggable={!isReadOnly && !fullyAssigned}
                    onDragStart={(event) =>
                      startApplicantDrag(
                        event,
                        dragIdsForApplicant(applicant.id),
                        "individual",
                      )
                    }
                    className={`flex items-center gap-2 rounded-xl border bg-white/90 p-2 shadow-sm ${fullyAssigned ? "border-amber-200/70 opacity-75" : selectedAllocationIds.includes(applicant.id) ? "cursor-grab border-violet-300 ring-1 ring-violet-200" : "cursor-grab border-amber-200/70 hover:border-amber-300 active:cursor-grabbing"}`}
                  >
                    {!fullyAssigned && !isReadOnly && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleAllocationSelection(applicant.id);
                        }}
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${selectedAllocationIds.includes(applicant.id) ? "border-violet-600 bg-violet-600 text-white" : "border-slate-300 bg-white"}`}
                        aria-label={`Select ${applicant.name}`}
                      >
                        {selectedAllocationIds.includes(applicant.id) && (
                          <Check className="h-3 w-3" />
                        )}
                      </button>
                    )}
                    <GenderAvatar
                      gender={applicant.gender}
                      name={applicant.name}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[10px] font-semibold text-slate-900">
                        {applicant.name}
                      </div>
                      <div className="text-[9px] text-slate-400">
                        ITS {applicant.itsNumber} · {applicant.age}y ·{" "}
                        {applicant.gender}
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-0.5">
                        {assignedRooms.map((room) => (
                          <Badge
                            key={room.id}
                            className="border-emerald-200 bg-emerald-100 px-1 py-0 text-[7px] text-emerald-700"
                          >
                            {room.city}: {room.type}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    {/* {!fullyAssigned && ( */}
                    {!isReadOnly && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await removeApplicantFromShortlist(applicant.id);
                          }}
                          className={`rounded p-1 transition-colors ${
                            isReadOnly
                              ? "cursor-not-allowed text-red-400 opacity-50"
                              : "text-red-500 hover:bg-red-50"
                          }`}
                          title="Remove applicant"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>

                        {!isReadOnly && (
                          <GripVertical className="h-4 w-4 text-amber-500" />
                        )}
                      </div>
                    )}
                    {/* )} */}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </aside>
      </div>

      {showAddApplicantDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeApplicantDialog}
        >
          <div
            className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-sky-100 bg-sky-50/70 p-5">
              <div>
                <h3 className="text-lg font-semibold text-sky-950">
                  Add Applicants
                </h3>
                <p className="mt-0.5 text-sm text-sky-600">
                  Select applicants with submitted visa applications
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-slate-500 hover:bg-white"
                onClick={closeApplicantDialog}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="border-b p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  disabled={isReadOnly}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  aria-label="Search applicants"
                  placeholder="Search by name, ITS number, reference ID or passport..."
                  className="h-10 border-sky-200 pl-10"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {isSearching ? (
                <div role="status" className="flex min-h-48 flex-col items-center justify-center gap-3 py-12 text-sm text-sky-700">
                  <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
                  <span>Loading applicants...</span>
                </div>
              ) : availableApplicants.length === 0 ? (
                <div className="py-12 text-center text-sm text-slate-400">
                  No submitted visa applicants available
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {availableApplicants.map((applicant) => {
                    const selected = pendingApplicantIds.includes(applicant.id);
                    return (
                      <button
                        key={applicant.id}
                        type="button"
                        onClick={() => {
                          if (isReadOnly) return;
                          setPendingApplicantIds((current) =>
                            selected
                              ? current.filter((id) => id !== applicant.id)
                              : [...current, applicant.id],
                          );
                        }}
                        className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${selected ? "border-sky-400 bg-sky-50" : "border-slate-200 bg-white hover:border-sky-200 hover:bg-sky-50/50"}`}
                      >
                        <GenderAvatar
                          gender={applicant.gender}
                          name={applicant.name}
                          size="lg"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-sm font-semibold text-slate-900">
                              {applicant.name}
                            </span>
                            {applicant.hofITS && (
                              <Badge className="bg-indigo-100 px-1.5 py-0 text-[9px] text-indigo-700">
                                {applicant.hofITS}
                              </Badge>
                            )}
                          </div>
                          <p className="flex items-center gap-1 whitespace-nowrap text-[10px] leading-4 text-slate-500">
                            <span className="shrink-0">
                              ITS {applicant.itsNumber} · {applicant.age}y ·{" "}
                              {applicant.gender}
                            </span>
                            {applicant.referenceId && (
                              <Badge
                                className="border-yellow-200 bg-yellow-100 px-1 py-0 text-[9px] leading-4 text-yellow-800"
                                title="Reference ID"
                              >
                                Ref: {applicant.referenceId}
                              </Badge>
                            )}
                          </p>
                        </div>
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${selected ? "border-sky-600 bg-sky-600 text-white" : "border-slate-300"}`}
                        >
                          {selected && <Check className="h-3.5 w-3.5" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between border-t bg-slate-50 p-4">
              <span className="text-xs text-slate-600">
                {pendingApplicantIds.length} selected
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={closeApplicantDialog}
                  disabled={isReadOnly}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={isReadOnly || pendingApplicantIds.length === 0}
                  className="bg-sky-600 text-white hover:bg-sky-700"
                  onClick={() => {
                    onAddApplicants(pendingApplicantIds);
                    closeApplicantDialog();
                  }}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add Selected
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
